import { useState, useEffect, useRef, useCallback } from "react";
import useViewPreference from "../hooks/useViewPreference";
import { useSearchParams } from "react-router-dom";
import StatsBar from "../components/StatsBar";
import { checkImageDPI } from "../lib/dpiCheck";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import ImageCard from "../components/ImageCard";
import {
  Search,
  MoreVertical,
  Check,
  X,
  FileText,
  Heart,
  Trash2,
  Images,
  LayoutGrid,
  List,
  Star,
  Download,
  Upload,
  Printer,
  Crosshair
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AllImages() {
  const { user } = useAuth();
  const isAffinity = user?.role === "affinity";
  const [searchParams] = useSearchParams();
  const [images, setImages] = useState([]);
  const [collections, setCollections] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "all");
  const [favoriteFilter, setFavoriteFilter] = useState(() => searchParams.get("favorites") === "true" ? "favorites" : "all");
  const { viewMode, setViewMode, imageSize, setImageSize } = useViewPreference("winners", "table", 3);
  
  // Detail modal state
  const [selectedImage, setSelectedImage] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [uploadingPrintReady, setUploadingPrintReady] = useState(false);
  const printReadyInputRef = useRef(null);
  const [printReadyBg, setPrintReadyBg] = useState("transparent");
  const [uploadingAffinity, setUploadingAffinity] = useState(false);
  const affinityInputRef = useRef(null);

  // Knockout generator state
  const [printReadyTab, setPrintReadyTab] = useState("upload");
  const [eyedropperActive, setEyedropperActive] = useState(false);
  const [knockoutColor, setKnockoutColor] = useState({ r: 255, g: 255, b: 255, hex: "#ffffff" });
  const [knockoutTolerance, setKnockoutTolerance] = useState(50);
  const [dotFrequency, setDotFrequency] = useState(8);
  const [dotAngle, setDotAngle] = useState(45);
  const [dotPattern, setDotPattern] = useState("dots");
  const [previewMode, setPreviewMode] = useState("result");
  const [sourceReady, setSourceReady] = useState(false);
  const knockoutCanvasRef = useRef(null);
  const sourceCanvasRef = useRef(null);

  const fetchImages = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== "all") params.status = statusFilter;
      if (favoriteFilter === "favorites") params.is_favorite = true;
      
      const { data } = await axios.get(`${API}/images`, { params, withCredentials: true });
      setImages(data);
    } catch (err) {
      toast.error("Failed to fetch images");
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionsAndUsers = async () => {
    try {
      const [collectionsRes, usersRes] = await Promise.all([
        axios.get(`${API}/collections`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);
      setCollections(collectionsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error("Failed to fetch collections/users");
    }
  };

  useEffect(() => {
    fetchCollectionsAndUsers();
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchImages, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [search, statusFilter, favoriteFilter]);

  const getCollectionName = (collectionId) => {
    const col = collections.find(c => c.id === collectionId);
    return col ? col.name : "-";
  };

  const toggleSelect = (imageId) => {
    setSelectedIds((prev) =>
      prev.includes(imageId) ? prev.filter((i) => i !== imageId) : [...prev, imageId]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === images.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(images.map((img) => img.id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    
    try {
      await axios.post(`${API}/images/bulk`, {
        image_ids: selectedIds,
        action
      }, { withCredentials: true });
      toast.success(`Action completed on ${selectedIds.length} images`);
      setSelectedIds([]);
      fetchImages();
    } catch (err) {
      toast.error("Bulk action failed");
    }
  };

  // Detail modal functions
  const openImageDetail = (image) => {
    setSelectedImage(image);
    setNewTitle(image.title || image.prompt || "");
    setEditingTitle(false);
    setDetailOpen(true);
  };

  const refreshImages = async () => {
    await fetchImages();
    if (selectedImage) {
      const { data } = await axios.get(`${API}/images`, { withCredentials: true });
      const updated = data.find(img => img.id === selectedImage.id);
      if (updated) setSelectedImage(updated);
    }
  };

  const [pushingToSwell, setPushingToSwell] = useState(false);

  const pushToSwell = async () => {
    if (!selectedImage) return;
    setPushingToSwell(true);
    try {
      const { data } = await axios.post(`${API}/images/${selectedImage.id}/push-to-swell`, {}, { withCredentials: true });
      toast.success(`Product created in Swell — ID: ${data.swell_product_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Failed to push to Swell", { duration: 10000 });
    } finally {
      setPushingToSwell(false);
    }
  };

  const updateStatus = async (status) => {
    if (!selectedImage) return;
    try {
      await axios.put(`${API}/images/${selectedImage.id}`, { status }, { withCredentials: true });
      toast.success(`Status updated to ${status}`);
      refreshImages();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const updateRating = async (rating) => {
    if (!selectedImage) return;
    try {
      await axios.put(`${API}/images/${selectedImage.id}`, { rating }, { withCredentials: true });
      toast.success("Rating updated");
      refreshImages();
    } catch (err) {
      toast.error("Failed to update rating");
    }
  };

  const updateAssignment = async (userId) => {
    if (!selectedImage) return;
    try {
      await axios.put(`${API}/images/${selectedImage.id}`, { assigned_to: userId }, { withCredentials: true });
      toast.success("Assignment updated");
      refreshImages();
    } catch (err) {
      toast.error("Failed to update assignment");
    }
  };

  const updateTitle = async () => {
    if (!selectedImage || !newTitle.trim()) return;
    try {
      await axios.put(`${API}/images/${selectedImage.id}`, { title: newTitle.trim() }, { withCredentials: true });
      toast.success("Title updated");
      setEditingTitle(false);
      refreshImages();
    } catch (err) {
      toast.error("Failed to update title");
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.trim() || !selectedImage) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/images/${selectedImage.id}/comments`, { text: comment }, { withCredentials: true });
      toast.success("Comment added");
      setComment("");
      refreshImages();
    } catch (err) {
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const formatTurnaround = (downloadedAt, uploadedAt) => {
    if (!downloadedAt || !uploadedAt) return null;
    const ms = new Date(uploadedAt) - new Date(downloadedAt);
    if (ms < 0) return null;
    const mins = Math.floor(ms / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h ${mins % 60}m`;
    if (hours > 0) return `${hours}h ${mins % 60}m`;
    return `${mins}m`;
  };

  // Reset knockout when a different image is opened
  useEffect(() => {
    setSourceReady(false);
    setEyedropperActive(false);
    setPrintReadyTab("upload");
  }, [selectedImage?.id]);

  // Load source image into hidden canvas for pixel sampling and knockout
  useEffect(() => {
    if (!selectedImage?.image_data) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const sc = sourceCanvasRef.current;
      if (!sc) return;
      sc.width = img.naturalWidth;
      sc.height = img.naturalHeight;
      sc.getContext("2d").drawImage(img, 0, 0);
      setSourceReady(true);
    };
    img.onerror = () => {
      // Retry without crossOrigin (data: URLs don't need it)
      const img2 = new Image();
      img2.onload = () => {
        const sc = sourceCanvasRef.current;
        if (!sc) return;
        sc.width = img2.naturalWidth;
        sc.height = img2.naturalHeight;
        sc.getContext("2d").drawImage(img2, 0, 0);
        setSourceReady(true);
      };
      img2.src = selectedImage.image_data;
    };
    img.src = selectedImage.image_data;
  }, [selectedImage?.id]);

  const hexToRgb = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return { r, g, b, hex };
  };

  const handleEyedropperImageClick = (e) => {
    if (!eyedropperActive) return;
    const sc = sourceCanvasRef.current;
    if (!sc || !sourceReady) return;
    const imgEl = e.currentTarget;
    const rect = imgEl.getBoundingClientRect();
    const px = Math.floor((e.clientX - rect.left) * (sc.width / rect.width));
    const py = Math.floor((e.clientY - rect.top) * (sc.height / rect.height));
    try {
      const [r, g, b] = sc.getContext("2d").getImageData(px, py, 1, 1).data;
      const hex = "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
      setKnockoutColor({ r, g, b, hex });
    } catch {
      toast.error("Cannot sample colour — image is cross-origin");
    }
    setEyedropperActive(false);
  };

  const processKnockout = useCallback(() => {
    const sc = sourceCanvasRef.current;
    const oc = knockoutCanvasRef.current;
    if (!sc || !oc || !sourceReady) return;

    const sw = sc.width, sh = sc.height;
    oc.width = sw;
    oc.height = sh;
    const ctx = oc.getContext("2d");
    ctx.clearRect(0, 0, sw, sh);

    if (previewMode === "original") {
      ctx.drawImage(sc, 0, 0);
      return;
    }

    let srcData;
    try {
      srcData = sc.getContext("2d").getImageData(0, 0, sw, sh);
    } catch {
      // Canvas tainted by cross-origin image — pixel access blocked
      ctx.drawImage(sc, 0, 0);
      return;
    }
    const { r: tr, g: tg, b: tb } = knockoutColor;

    // Pass 1: colour knockout for fully opaque pixels
    const outData = ctx.createImageData(sw, sh);
    for (let i = 0; i < srcData.data.length; i += 4) {
      const r = srcData.data[i], g = srcData.data[i+1], b = srcData.data[i+2], a = srcData.data[i+3];
      if (a === 255) {
        const dist = Math.sqrt((r-tr)**2 + (g-tg)**2 + (b-tb)**2);
        if (dist >= knockoutTolerance) {
          outData.data[i] = r; outData.data[i+1] = g; outData.data[i+2] = b; outData.data[i+3] = 255;
        }
      }
    }
    ctx.putImageData(outData, 0, 0);

    // Pass 2: halftone dots/lines for semi-transparent pixels
    const spacing = Math.max(3, Math.round(100 / dotFrequency));
    const angleRad = (dotAngle * Math.PI) / 180;
    const cosA = Math.cos(angleRad);
    const sinA = Math.sin(angleRad);
    const diag = Math.ceil(Math.sqrt(sw * sw + sh * sh));

    for (let gy = -diag; gy < diag; gy += spacing) {
      for (let gx = -diag; gx < diag; gx += spacing) {
        const cx = Math.round(cosA * gx - sinA * gy + sw / 2);
        const cy = Math.round(sinA * gx + cosA * gy + sh / 2);
        if (cx < 0 || cx >= sw || cy < 0 || cy >= sh) continue;

        const half = Math.floor(spacing / 2);
        let rS = 0, gS = 0, bS = 0, aS = 0, n = 0;
        for (let dy = -half; dy <= half; dy++) {
          for (let dx = -half; dx <= half; dx++) {
            const px = cx + dx, py = cy + dy;
            if (px < 0 || px >= sw || py < 0 || py >= sh) continue;
            const idx = (py * sw + px) * 4;
            const a = srcData.data[idx + 3];
            if (a > 0 && a < 255) {
              rS += srcData.data[idx]; gS += srcData.data[idx+1]; bS += srcData.data[idx+2]; aS += a; n++;
            }
          }
        }
        if (n === 0) continue;
        const avgA = aS / n;
        const radius = (spacing / 2) * (avgA / 255);
        if (radius < 0.3) continue;
        ctx.fillStyle = `rgb(${Math.round(rS/n)},${Math.round(gS/n)},${Math.round(bS/n)})`;
        if (dotPattern === "dots") {
          ctx.beginPath();
          ctx.arc(cx, cy, radius, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(angleRad);
          ctx.fillRect(-radius, -spacing / 2, radius * 2, spacing);
          ctx.restore();
        }
      }
    }
  }, [knockoutColor, knockoutTolerance, dotFrequency, dotAngle, dotPattern, previewMode, sourceReady]);

  // Live preview — re-runs whenever controls or source change
  useEffect(() => {
    if (printReadyTab === "knockout" && sourceReady) processKnockout();
  }, [processKnockout, sourceReady, printReadyTab]);

  const downloadKnockoutLayer = () => {
    const canvas = knockoutCanvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `knockout-layer-${selectedImage.id}.png`;
    link.click();
  };

  const handleDownload = async () => {
    if (!selectedImage) return;
    try {
      await axios.post(`${API}/images/${selectedImage.id}/track-download`, {}, { withCredentials: true });
      refreshImages();
    } catch {}
    const link = document.createElement("a");
    link.href = selectedImage.image_data;
    link.download = `tshirt-design-${selectedImage.id}.png`;
    link.click();
  };

  const handlePrintReadyUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedImage) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // DPI check (reads only header bytes — fast even on large files)
    const { dpi, message } = await checkImageDPI(file);
    if (dpi === null) {
      toast.warning(`DPI unknown — ${message}. Recommended: 300 DPI for print.`);
    } else if (dpi < 300) {
      const proceed = window.confirm(
        `This image is ${dpi} DPI. 300 DPI is the industry standard for t-shirt printing.\n\nLow DPI may result in blurry or pixelated prints.\n\nUpload anyway?`
      );
      if (!proceed) {
        if (printReadyInputRef.current) printReadyInputRef.current.value = "";
        return;
      }
      toast.warning(`Uploaded at ${dpi} DPI — below the 300 DPI standard.`);
    } else {
      toast.success(`DPI check passed: ${dpi} DPI ✓`);
    }

    setUploadingPrintReady(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (dpi !== null) formData.append("dpi", String(dpi));
      await axios.post(`${API}/images/${selectedImage.id}/print-ready`, formData);
      toast.success("Print Ready image uploaded");
      refreshImages();
    } catch (err) {
      toast.error("Failed to upload Print Ready image");
    } finally {
      setUploadingPrintReady(false);
      if (printReadyInputRef.current) printReadyInputRef.current.value = "";
    }
  };

  const downloadPrintReady = () => {
    if (!selectedImage?.print_ready_image) return;
    const link = document.createElement("a");
    link.href = selectedImage.print_ready_image;
    link.download = `print-ready-${selectedImage.id}.png`;
    link.click();
  };

  const handleAffinityUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAffinity(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/images/${selectedImage.id}/affinity-file`, formData);
      setSelectedImage(res.data);
      refreshImages();
      toast.success("Affinity file uploaded");
    } catch {
      toast.error("Failed to upload Affinity file");
    } finally {
      setUploadingAffinity(false);
      if (affinityInputRef.current) affinityInputRef.current.value = "";
    }
  };

  const downloadAffinityFile = () => {
    if (!selectedImage?.affinity_file) return;
    const link = document.createElement("a");
    link.href = selectedImage.affinity_file;
    link.download = `affinity-${selectedImage.id}`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  return (
    <div className="pb-20 md:pb-0" data-testid="all-images-page">
      <StatsBar />
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            Winners
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {isAffinity ? "Images marked for Affinity review" : "Browse and manage all your generated designs"}
          </p>
        </div>
        
        {/* View Toggle & Size Control */}
        <div className="flex items-center gap-4">
          {viewMode === "grid" && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Size:</span>
              <input
                type="range"
                min="1"
                max="4"
                value={imageSize}
                onChange={(e) => setImageSize(Number(e.target.value))}
                className="w-20 h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          )}
          
          <div className="flex border border-gray-200">
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 ${viewMode === "table" ? "bg-black text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              data-testid="view-table"
            >
              <List size={18} />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 ${viewMode === "grid" ? "bg-black text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              data-testid="view-grid"
            >
              <LayoutGrid size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {isAffinity && (
        <div className="mb-4 px-3 py-2 bg-gray-50 border border-gray-200 text-xs text-gray-500 uppercase tracking-widest">
          Showing Affinity images
        </div>
      )}
      <div className={`flex flex-wrap items-center gap-4 mb-6 p-4 border border-gray-200 bg-gray-50 ${isAffinity ? "hidden" : ""}`}>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search prompts..."
            className="pl-10 rounded-none border-gray-300"
            data-testid="search-images"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32 rounded-none border-gray-300" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="affinity">Affinity</SelectItem>
            <SelectItem value="print-ready">Print Ready</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>

        <Select value={favoriteFilter} onValueChange={setFavoriteFilter}>
          <SelectTrigger className="w-32 rounded-none border-gray-300" data-testid="favorite-filter">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Winners</SelectItem>
            <SelectItem value="favorites">Favorites</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Selection & Bulk Actions */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={selectedIds.length === images.length && images.length > 0}
            onCheckedChange={selectAll}
            data-testid="select-all"
          />
          <span className="text-sm text-gray-500">
            {selectedIds.length > 0 ? `${selectedIds.length} selected` : `${images.length} images`}
          </span>
        </div>

        {selectedIds.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-none" data-testid="bulk-actions">
                <MoreVertical size={16} className="mr-2" />
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleBulkAction("approve")} data-testid="bulk-approve">
                <Check size={14} className="mr-2 text-green-600" />
                Approve
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkAction("reject")} data-testid="bulk-reject">
                <X size={14} className="mr-2 text-red-600" />
                Reject
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkAction("draft")} data-testid="bulk-draft">
                <FileText size={14} className="mr-2 text-yellow-600" />
                Set Draft
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkAction("favorite")} data-testid="bulk-favorite">
                <Heart size={14} className="mr-2 text-pink-600" />
                Favorite
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkAction("unfavorite")} data-testid="bulk-unfavorite">
                <Heart size={14} className="mr-2" />
                Unfavorite
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkAction("delete")} className="text-red-600" data-testid="bulk-delete">
                <Trash2 size={14} className="mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Images */}
      {images.length === 0 ? (
        <div className="border border-dashed border-gray-300 p-8 text-center">
          <Images className="mx-auto text-gray-400 mb-3" size={32} />
          <p className="text-gray-500 text-sm">
            {search || statusFilter !== "all" || favoriteFilter !== "all" 
              ? "No images match your filters" 
              : "No images yet"}
          </p>
        </div>
      ) : viewMode === "table" ? (
        /* Table View */
        <div className="border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="w-10 p-3"></th>
                  <th className="text-left p-3 font-medium text-xs uppercase tracking-widest text-gray-500">Image</th>
                  <th className="text-left p-3 font-medium text-xs uppercase tracking-widest text-gray-500">Name</th>
                  <th className="text-left p-3 font-medium text-xs uppercase tracking-widest text-gray-500 hidden md:table-cell">Collection</th>
                  <th className="text-left p-3 font-medium text-xs uppercase tracking-widest text-gray-500">Status</th>
                  <th className="text-left p-3 font-medium text-xs uppercase tracking-widest text-gray-500">Assigned</th>
                  <th className="text-left p-3 font-medium text-xs uppercase tracking-widest text-gray-500 hidden md:table-cell">Rating</th>
                </tr>
              </thead>
              <tbody>
                {images.map((img, index) => (
                  <tr 
                    key={img.id} 
                    onClick={() => openImageDetail(img)}
                    className={`border-b border-gray-100 hover:bg-blue-50 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                  >
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(img.id)}
                        onCheckedChange={() => toggleSelect(img.id)}
                      />
                    </td>
                    <td className="p-2">
                      <img 
                        src={img.image_data}
                        alt={img.prompt}
                        className="w-10 h-10 object-cover"
                      />
                    </td>
                    <td className="p-3">
                      <p className="truncate max-w-[150px] text-gray-700">{img.title || img.prompt}</p>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <span className="text-gray-500">{getCollectionName(img.collection_id)}</span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 text-xs uppercase ${
                        img.status === 'approved' ? 'status-approved' :
                        img.status === 'rejected' ? 'status-rejected' :
                        img.status === 'affinity' ? 'status-affinity' :
                        img.status === 'print-ready' ? 'status-print-ready' : 'status-draft'
                      }`}>
                        {img.status}
                      </span>
                    </td>
                    <td className="p-3">
                      {img.assigned_user_name ? (
                        <div className="flex items-center gap-1">
                          <div className="w-5 h-5 bg-black text-white flex items-center justify-center text-[10px] font-bold">
                            {img.assigned_user_name.charAt(0)}
                          </div>
                          <span className="text-xs">{img.assigned_user_name}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(star => (
                          <span key={star} className={`text-xs ${star <= (img.rating || 0) ? 'text-yellow-400' : 'text-gray-200'}`}>★</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Grid View */
        <div className={`grid gap-4 ${
          imageSize === 1 ? 'grid-cols-4 md:grid-cols-6 lg:grid-cols-8' :
          imageSize === 2 ? 'grid-cols-3 md:grid-cols-5 lg:grid-cols-6' :
          imageSize === 3 ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' :
          'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        }`}>
          {images.map((image) => (
            <ImageCard
              key={image.id}
              image={image}
              selected={selectedIds.includes(image.id)}
              onSelect={() => toggleSelect(image.id)}
              onUpdate={fetchImages}
            />
          ))}
        </div>
      )}

      {/* Image Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl rounded-none max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-black">Image Details</DialogTitle>
          </DialogHeader>
          
          {selectedImage && (
            <div className="space-y-6">
              {/* Images Row - Always Side by Side */}
              <div className="flex gap-4">
                {/* Original Image */}
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">{selectedImage.is_upscaled ? "Upscaled Image" : "Original Image"}</p>
                  <img
                    src={selectedImage.image_data}
                    alt={selectedImage.prompt}
                    className={`w-full border border-gray-200 ${eyedropperActive ? "cursor-crosshair ring-2 ring-yellow-400 ring-offset-1" : ""}`}
                    onClick={handleEyedropperImageClick}
                  />
                  <Button
                    onClick={handleDownload}
                    variant="outline"
                    className="w-full mt-2 rounded-none"
                    size="sm"
                  >
                    <Download size={14} className="mr-2" />
                    {selectedImage.is_upscaled ? "Download Upscaled" : "Download Original"}
                  </Button>
                </div>

                {/* Print Ready Image */}
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-2">
                    <Printer size={12} />
                    Print Ready
                    {selectedImage.print_ready_uploaded_at && (
                      <span className="normal-case text-gray-400 font-normal">
                        — uploaded {new Date(selectedImage.print_ready_uploaded_at).toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'2-digit' })}
                      </span>
                    )}
                  </p>

                  {/* Tab toggle */}
                  <div className="flex border border-gray-200 mb-3">
                    <button
                      onClick={() => setPrintReadyTab("upload")}
                      className={`flex-1 py-1.5 text-[10px] uppercase tracking-widest transition-colors ${printReadyTab === "upload" ? "bg-black text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                    >
                      Upload
                    </button>
                    <button
                      onClick={() => setPrintReadyTab("knockout")}
                      className={`flex-1 py-1.5 text-[10px] uppercase tracking-widest transition-colors ${printReadyTab === "knockout" ? "bg-black text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                    >
                      Knockout Generator
                    </button>
                  </div>

                  {/* Upload tab */}
                  {printReadyTab === "upload" && (
                    <>
                      {selectedImage.print_ready_image ? (
                        <>
                          <div
                            className={`border-2 border-green-500 ${
                              printReadyBg === "white" ? "bg-white" :
                              printReadyBg === "black" ? "bg-black" :
                              "bg-checkerboard"
                            }`}
                          >
                            <img
                              src={selectedImage.print_ready_image}
                              alt="Print Ready"
                              className="w-full"
                            />
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            {selectedImage.print_ready_dpi != null ? (
                              <div className={`px-2 py-1 text-xs font-medium inline-flex items-center gap-1 ${
                                selectedImage.print_ready_dpi >= 300
                                  ? "bg-green-50 border border-green-300 text-green-700"
                                  : "bg-red-50 border border-red-300 text-red-700"
                              }`}>
                                {selectedImage.print_ready_dpi >= 300 ? "✓" : "⚠"} {selectedImage.print_ready_dpi} DPI
                                {selectedImage.print_ready_dpi < 300 && " — below 300"}
                              </div>
                            ) : (
                              <div className="px-2 py-1 text-xs text-gray-400 border border-gray-200">DPI unknown</div>
                            )}
                            <div className="flex gap-1">
                              {["transparent", "white", "black"].map((bg) => (
                                <button
                                  key={bg}
                                  onClick={() => setPrintReadyBg(bg)}
                                  title={bg.charAt(0).toUpperCase() + bg.slice(1)}
                                  className={`w-5 h-5 border flex-shrink-0 transition-all ${
                                    printReadyBg === bg ? "ring-2 ring-offset-1 ring-blue-500" : "opacity-50 hover:opacity-100"
                                  } ${
                                    bg === "transparent" ? "bg-checkerboard" :
                                    bg === "white" ? "bg-white" : "bg-black"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Button onClick={downloadPrintReady} variant="outline" className="flex-1 rounded-none" size="sm">
                              <Download size={14} className="mr-2" />Download
                            </Button>
                            <Button onClick={() => printReadyInputRef.current?.click()} variant="outline" className="flex-1 rounded-none" size="sm">
                              Replace
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div
                          onClick={() => printReadyInputRef.current?.click()}
                          className="border-2 border-dashed border-gray-300 p-8 text-center cursor-pointer hover:border-gray-400 transition-colors aspect-square flex flex-col items-center justify-center"
                        >
                          <Upload className="text-gray-400 mb-2" size={32} />
                          <p className="text-sm text-gray-500">
                            {uploadingPrintReady ? "Uploading..." : "Upload Print Ready"}
                          </p>
                          {!uploadingPrintReady && (
                            <p className="text-xs text-gray-400 mt-1">300 DPI recommended</p>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Knockout Generator tab */}
                  {printReadyTab === "knockout" && (
                    <div className="space-y-3">
                      {/* Preview mode toggle */}
                      <div className="flex border border-gray-200">
                        {["result", "original"].map((mode) => (
                          <button
                            key={mode}
                            onClick={() => setPreviewMode(mode)}
                            className={`flex-1 py-1 text-[10px] uppercase tracking-widest transition-colors ${previewMode === mode ? "bg-black text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                          >
                            {mode === "result" ? "Knockout" : "Original"}
                          </button>
                        ))}
                      </div>

                      {/* Canvas preview */}
                      <div className="border border-gray-200 bg-checkerboard relative overflow-hidden" style={{ minHeight: "180px" }}>
                        <canvas ref={knockoutCanvasRef} className="max-w-full block" />
                        {!sourceReady && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <p className="text-xs text-gray-400">Loading…</p>
                          </div>
                        )}
                      </div>

                      {/* Colour picker row */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEyedropperActive((v) => !v)}
                          title="Click image to sample colour"
                          className={`p-1.5 border transition-colors flex-shrink-0 ${eyedropperActive ? "bg-yellow-400 border-yellow-500 text-black" : "border-gray-200 hover:border-gray-400"}`}
                        >
                          <Crosshair size={13} />
                        </button>
                        <div className="w-7 h-7 border border-gray-300 flex-shrink-0" style={{ background: knockoutColor.hex }} />
                        <input
                          type="color"
                          value={knockoutColor.hex}
                          onChange={(e) => setKnockoutColor(hexToRgb(e.target.value))}
                          className="w-7 h-7 p-0 border-0 cursor-pointer flex-shrink-0"
                          title="Pick colour"
                        />
                        <span className="text-[10px] font-mono text-gray-500">{knockoutColor.hex.toUpperCase()}</span>
                        {eyedropperActive && (
                          <span className="text-[10px] text-yellow-600 font-medium ml-auto">← Click image</span>
                        )}
                      </div>

                      {/* Sliders */}
                      <div className="space-y-2">
                        {[
                          { label: "Tolerance", value: knockoutTolerance, min: 10, max: 200, set: setKnockoutTolerance, unit: "" },
                          { label: "Dot Frequency", value: dotFrequency, min: 2, max: 20, set: setDotFrequency, unit: "" },
                          { label: "Dot Angle", value: dotAngle, min: 0, max: 90, set: setDotAngle, unit: "°" },
                        ].map(({ label, value, min, max, set, unit }) => (
                          <div key={label}>
                            <div className="flex justify-between mb-0.5">
                              <label className="text-[10px] uppercase tracking-widest text-gray-500">{label}</label>
                              <span className="text-[10px] text-gray-700 tabular-nums">{value}{unit}</span>
                            </div>
                            <input
                              type="range"
                              min={min}
                              max={max}
                              value={value}
                              onChange={(e) => set(+e.target.value)}
                              className="w-full h-1 accent-black"
                            />
                          </div>
                        ))}
                      </div>

                      {/* Pattern toggle */}
                      <div className="flex border border-gray-200">
                        {["dots", "lines"].map((p) => (
                          <button
                            key={p}
                            onClick={() => setDotPattern(p)}
                            className={`flex-1 py-1 text-[10px] uppercase tracking-widest transition-colors ${dotPattern === p ? "bg-black text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>

                      {/* Reset + Download */}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            setKnockoutColor({ r:255, g:255, b:255, hex:"#ffffff" });
                            setKnockoutTolerance(50);
                            setDotFrequency(8);
                            setDotAngle(45);
                            setDotPattern("dots");
                            setPreviewMode("result");
                          }}
                          variant="outline" size="sm" className="flex-1 rounded-none"
                        >
                          Reset
                        </Button>
                        <Button onClick={downloadKnockoutLayer} disabled={!sourceReady} variant="outline" size="sm" className="flex-1 rounded-none">
                          <Download size={13} className="mr-1" />Download Layer
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Affinity File — always visible */}
                  <Button
                    onClick={selectedImage.affinity_file ? downloadAffinityFile : () => affinityInputRef.current?.click()}
                    variant="outline"
                    className="w-full mt-2 rounded-none"
                    size="sm"
                    disabled={uploadingAffinity}
                  >
                    {selectedImage.affinity_file ? (
                      <><Download size={14} className="mr-2" />Download Affinity File</>
                    ) : (
                      <><Upload size={14} className="mr-2" />{uploadingAffinity ? "Uploading..." : "Upload Affinity File"}</>
                    )}
                  </Button>
                  {selectedImage.affinity_file_uploaded_at && (
                    <p className="text-xs text-gray-400 mt-1">
                      Uploaded {new Date(selectedImage.affinity_file_uploaded_at).toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'2-digit' })}
                    </p>
                  )}
                  <input
                    type="file"
                    ref={affinityInputRef}
                    onChange={handleAffinityUpload}
                    accept=".afdesign,.afphoto,.afpub,.pdf"
                    className="hidden"
                  />
                </div>
              </div>

              <input
                type="file"
                ref={printReadyInputRef}
                onChange={handlePrintReadyUpload}
                accept="image/*"
                className="hidden"
              />
              <canvas ref={sourceCanvasRef} style={{ display: "none" }} />

              {/* Turnaround Time */}
              {(selectedImage.image_downloaded_at || selectedImage.print_ready_uploaded_at) && (
                <div className="flex items-center gap-6 px-4 py-3 bg-gray-50 border border-gray-200 text-xs">
                  <div>
                    <p className="text-gray-400 uppercase tracking-widest mb-0.5">Downloaded</p>
                    <p className="font-medium">{selectedImage.image_downloaded_at ? new Date(selectedImage.image_downloaded_at).toLocaleString() : "—"}</p>
                  </div>
                  <div className="text-gray-300">→</div>
                  <div>
                    <p className="text-gray-400 uppercase tracking-widest mb-0.5">Print Ready Uploaded</p>
                    <p className="font-medium">{selectedImage.print_ready_uploaded_at ? new Date(selectedImage.print_ready_uploaded_at).toLocaleString() : "—"}</p>
                  </div>
                  {formatTurnaround(selectedImage.image_downloaded_at, selectedImage.print_ready_uploaded_at) && (
                    <>
                      <div className="text-gray-300">=</div>
                      <div>
                        <p className="text-gray-400 uppercase tracking-widest mb-0.5">Turnaround</p>
                        <p className="font-bold text-black">{formatTurnaround(selectedImage.image_downloaded_at, selectedImage.print_ready_uploaded_at)}</p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Details Section */}
              <div className="grid md:grid-cols-2 gap-6 pt-4 border-t border-gray-200">
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs uppercase tracking-widest text-gray-500">Title</p>
                      {!editingTitle && (
                        <button 
                          onClick={() => setEditingTitle(true)}
                          className="text-xs text-gray-500 hover:text-black underline"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {editingTitle ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 bg-white text-black"
                          autoFocus
                        />
                        <Button
                          onClick={updateTitle}
                          size="sm"
                          className="rounded-none bg-black text-white hover:bg-gray-800 px-3"
                        >
                          Save
                        </Button>
                        <Button
                          onClick={() => {
                            setEditingTitle(false);
                            setNewTitle(selectedImage.title || selectedImage.prompt || "");
                          }}
                          size="sm"
                          variant="outline"
                          className="rounded-none px-3"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-black">{selectedImage.title || selectedImage.prompt || "-"}</p>
                    )}
                  </div>

                  {selectedImage.original_filename && (
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Original Filename</p>
                      <p className="text-sm text-gray-600 break-all">{selectedImage.original_filename}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Status</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => updateStatus("draft")}
                        className={`px-3 py-1 text-xs uppercase border ${selectedImage.status === 'draft' ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'border-gray-200 hover:border-gray-400'}`}
                      >
                        Draft
                      </button>
                      <button
                        onClick={() => updateStatus("affinity")}
                        className={`px-3 py-1 text-xs uppercase border ${selectedImage.status === 'affinity' ? 'bg-purple-100 border-purple-400 text-purple-700' : 'border-gray-200 hover:border-gray-400'}`}
                      >
                        Affinity
                      </button>
                      <button
                        onClick={() => selectedImage.print_ready_image && updateStatus("print-ready")}
                        disabled={!selectedImage.print_ready_image}
                        className={`px-3 py-1 text-xs uppercase border ${
                          selectedImage.status === 'print-ready' ? 'bg-sky-100 border-sky-400 text-sky-700' : 
                          !selectedImage.print_ready_image ? 'border-gray-100 text-gray-300 cursor-not-allowed' :
                          'border-gray-200 hover:border-gray-400'
                        }`}
                        title={!selectedImage.print_ready_image ? "Upload print ready image first" : ""}
                      >
                        Print Ready
                      </button>
                      <button
                        onClick={() => updateStatus("approved")}
                        className={`px-3 py-1 text-xs uppercase border ${selectedImage.status === 'approved' ? 'bg-green-100 border-green-400 text-green-700' : 'border-gray-200 hover:border-gray-400'}`}
                      >
                        Approved
                      </button>
                      <button
                        onClick={() => updateStatus("rejected")}
                        className={`px-3 py-1 text-xs uppercase border ${selectedImage.status === 'rejected' ? 'bg-red-100 border-red-400 text-red-700' : 'border-gray-200 hover:border-gray-400'}`}
                      >
                        Rejected
                      </button>
                    </div>
                  </div>

                  {!isAffinity && selectedImage.status === 'approved' && (
                    <div className="pt-2">
                      <button
                        onClick={pushToSwell}
                        disabled={pushingToSwell}
                        className="w-full px-4 py-2 text-xs uppercase tracking-widest font-bold bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {pushingToSwell ? 'Pushing to Swell...' : 'Push to Swell →'}
                      </button>
                    </div>
                  )}

                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Rating</p>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => updateRating(star)}
                          className="p-1"
                        >
                          <Star
                            size={20}
                            className={star <= (selectedImage.rating || 0) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Assignment */}
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Assign To</p>
                    <Select 
                      value={selectedImage.assigned_to || "unassigned"} 
                      onValueChange={(val) => updateAssignment(val === "unassigned" ? "" : val)}
                    >
                      <SelectTrigger className="rounded-none border-gray-300 bg-white text-black">
                        <SelectValue placeholder="Select person" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Comments */}
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">
                      Comments {selectedImage.comments?.length > 0 && `(${selectedImage.comments.length})`}
                    </p>
                    
                    {selectedImage.comments?.length > 0 && (
                      <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
                        {selectedImage.comments.map((c, i) => (
                          <div key={i} className="p-2 bg-gray-50 border border-gray-200 text-sm">
                            <p className="font-medium text-xs text-black">{c.user_name}</p>
                            <p className="text-gray-600">{c.text}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <form onSubmit={addComment} className="flex gap-2">
                      <Textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Add a comment..."
                        className="rounded-none border-gray-300 bg-white text-black resize-none text-sm"
                        rows={2}
                      />
                      <Button 
                        type="submit" 
                        disabled={submitting || !comment.trim()}
                        className="rounded-none bg-black text-white hover:bg-gray-800 self-end"
                      >
                        {submitting ? "..." : "Add"}
                      </Button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
